// components/TabComponents/QuotedTab.jsx
import React, { useState, useRef } from 'react';
import {
    Card, Row, Col, Typography, Button, Tag, Alert,
    Timeline, message, Modal, Form, InputNumber, Badge
} from 'antd';
import {
    DollarOutlined, PlusOutlined, FileSearchOutlined,
    ExclamationCircleOutlined, UserOutlined
} from '@ant-design/icons';
import API from '../../../../API/API';
import { formatPSTDate } from '../../utils/dateUtils';
import { getActorName } from '../../utils/actorUtils';
import { getQuoteHistory, QUOTE_SOURCE_TOTAL_AMOUNT } from '../../utils/quoteHistoryUtils';

const { Title, Text } = Typography;

const QuotedTab = ({ lead, agentMap, onUpdateSuccess }) => {
    const [loading, setLoading] = useState(false);
    const priceRef = useRef('');
    const [samePrice, setSamePrice] = useState(false);
    const [amountInWords, setAmountInWords] = useState("");

    const handleAddQuote = () => {
        priceRef.current = '';
        setSamePrice(false);
        setAmountInWords("");

        const currentQuotes = lead.quotedValue || [];
        const lastQuotePrice = currentQuotes.length > 0
            ? currentQuotes[currentQuotes.length - 1].price
            : null;

        Modal.confirm({
            title: 'Add New Quote',
            width: 500,
            okText: 'Add Quote',
            cancelText: 'Cancel',

            content: (
                <div style={{ marginTop: 16 }}>
                    <Form layout="vertical">
                        <Form.Item label="Quote Amount ($)" required>
                            <InputNumber
                                style={{ width: '100%' }}
                                placeholder="Enter amount"
                                prefix={<DollarOutlined />}
                                min={0.01}
                                step={0.01}
                                formatter={(value) =>
                                    `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                                }
                                parser={(value) =>
                                    value.replace(/\$\s?|(,*)/g, '')
                                }
                                onChange={(value) => {
                                    const numericValue = Number(value || 0);
                                    priceRef.current = numericValue || "";

                                    let duplicateFound = false;
                                    let duplicateMessage = "";

                                    // Check against last quote
                                    if (lastQuotePrice !== null &&
                                        Math.abs(numericValue - Number(lastQuotePrice)) < 0.01) {
                                        duplicateFound = true;
                                        duplicateMessage = `Entered amount is same as last quote ($${lastQuotePrice}).`;
                                    }

                                    // Check against advance payments
                                    const inAdvancePayments = amountExistsInAdvancePayments(lead, numericValue);

                                    if (inAdvancePayments) {
                                        duplicateFound = true;
                                        duplicateMessage = `Amount $${numericValue} already exists in advance payments.`;
                                    }

                                    setSamePrice(duplicateFound);

                                    if (numericValue > 1_000_000) {
                                        message.warning('Amount seems unusually high. Please verify.');
                                    }

                                    setAmountInWords(
                                        numericValue ? numberToWords(numericValue) : ""
                                    );
                                }}
                            />
                        </Form.Item>

                        {/* Show amount in words */}
                        {amountInWords && (
                            <div style={{
                                marginTop: "2px",
                                fontSize: "12px",
                                color: "#389e0d",
                                fontStyle: "italic",
                            }}>
                                {amountInWords} Only
                            </div>
                        )}

                        {/* Show duplicate warning */}
                        {samePrice && (
                            <Alert
                                message="Duplicate amount detected"
                                description="This amount already exists in either last quote or advance payments. Please enter a different amount."
                                type="warning"
                                showIcon
                                style={{ marginTop: 8 }}
                            />
                        )}
                    </Form>
                </div>
            ),

            okButtonProps: {
                disabled: samePrice
            },

            onOk: async () => {
                if (!priceRef.current) {
                    message.error('Please enter quote amount');
                    return false;
                }

                const newPrice = Number(priceRef.current);

                // Final check before submitting
                const lastQuoteCheck = lastQuotePrice !== null &&
                    Math.abs(newPrice - Number(lastQuotePrice)) < 0.01;
                const advanceCheck = amountExistsInAdvancePayments(lead, newPrice);

                if (lastQuoteCheck || advanceCheck) {
                    message.error('Cannot add duplicate amount!');
                    return false;
                }

                try {
                    const hideLoading = message.loading('Adding quote...', 0);

                    // Only the new quote is sent: the server appends it to the
                    // full history and stamps who added it and when.
                    const payload = {
                        quotedValue: [{ price: newPrice }]
                    };

                    const response = await API.UpdateLead(lead._id, payload);

                    hideLoading();

                    if (response?.status) {
                        message.success('Quote added successfully!');
                        onUpdateSuccess(); // Call parent function to refresh lead data
                        return true;
                    } else {
                        message.error(
                            response?.message ||
                            response?.data?.message ||
                            'Failed to add quote'
                        );
                        return false;
                    }
                } catch (error) {
                    console.error('Error adding quote:', error);
                    message.error('Failed to add quote');
                    return false;
                }
            }
        });
    };

    // Helper function to convert number to words
    const numberToWords = (num) => {
        if (!num || isNaN(num)) return "";

        const ones = [
            "", "One", "Two", "Three", "Four", "Five", "Six",
            "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve",
            "Thirteen", "Fourteen", "Fifteen", "Sixteen",
            "Seventeen", "Eighteen", "Nineteen",
        ];
        const tens = [
            "", "", "Twenty", "Thirty", "Forty",
            "Fifty", "Sixty", "Seventy", "Eighty", "Ninety",
        ];

        const toWords = (n) => {
            if (n < 20) return ones[n];
            if (n < 100)
                return tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : "");
            if (n < 1000)
                return (
                    ones[Math.floor(n / 100)] +
                    " Hundred" +
                    (n % 100 ? " " + toWords(n % 100) : "")
                );
            if (n < 1_000_000)
                return (
                    toWords(Math.floor(n / 1000)) +
                    " Thousand" +
                    (n % 1000 ? " " + toWords(n % 1000) : "")
                );
            if (n < 1_000_000_000)
                return (
                    toWords(Math.floor(n / 1_000_000)) +
                    " Million" +
                    (n % 1_000_000 ? " " + toWords(n % 1_000_000) : "")
                );
            return "Amount too large";
        };

        const dollars = Math.floor(num);
        const cents = Math.round((num - dollars) * 100);

        let result = `${toWords(dollars)} Dollar${dollars !== 1 ? "s" : ""}`;
        if (cents > 0) {
            result += ` and ${toWords(cents)} Cent${cents !== 1 ? "s" : ""}`;
        }

        return result;
    };

    // Helper function to check if amount exists in advance payments
    const amountExistsInAdvancePayments = (lead, amount) => {
        if (!lead.advancePayments || lead.advancePayments.length === 0) return false;

        return lead.advancePayments.some(payment => {
            const paymentAmount = Number(payment.amount || 0);
            const newAmount = Number(amount || 0);
            return Math.abs(paymentAmount - newAmount) < 0.01; // Allow for floating point precision
        });
    };

    const formatPrice = (price) => `$${Number(price || 0).toFixed(2)}`;

    return (
        <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <Title level={4} style={{ margin: 0 }}>Quote History</Title>
               {(
    lead?.leadStage === "Quoted" ||
    lead?.leadStage === "Pending Payment" ||
    lead?.leadStage === "Payment Details" ||
    lead?.leadStage === "Payment Completed" ||
    lead?.leadStage === "Sales Made"
) && (
    <Button
        type="primary"
        onClick={handleAddQuote}
        icon={<PlusOutlined />}
    >
        Add Quote
    </Button>
)}
            </div>

            {(!lead.quotedValue || lead.quotedValue.length === 0) ? (
                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                    <FileSearchOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
                    <Text type="secondary" style={{ display: 'block', marginBottom: '24px' }}>
                        No quotes added yet
                    </Text>
                    <Button
                        type="primary"
                        onClick={handleAddQuote}
                        icon={<PlusOutlined />}
                    >
                        Add First Quote
                    </Button>
                </div>
            ) : (
                <Timeline>
                    {getQuoteHistory(lead.quotedValue) // newest first, all quotes
                        .map((quote, index) => {
                            const isLatest = index === 0;
                            const isFirstQuote = quote.previousPrice === undefined;
                            const actorName = getActorName(quote.changedBy, agentMap) || 'Unknown';
                            const difference = isFirstQuote
                                ? 0
                                : Number(quote.price) - Number(quote.previousPrice);

                            return (
                                <Timeline.Item
                                    key={quote._id || quote.quoteNumber}
                                    color={isLatest ? 'green' : 'blue'}
                                >
                                    <Card
                                        size="small"
                                        style={{
                                            borderLeft: isLatest ? '3px solid #52c41a' : '1px solid #f0f0f0',
                                            backgroundColor: isLatest ? '#f6ffed' : 'white'
                                        }}
                                    >
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <div style={{ flex: 1 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                                                    <Text strong>Quote #{quote.quoteNumber}</Text>
                                                    {isLatest && (
                                                        <Tag color="green" icon={<ExclamationCircleOutlined />}>
                                                            Latest
                                                        </Tag>
                                                    )}
                                                    {quote.source === QUOTE_SOURCE_TOTAL_AMOUNT && (
                                                        <Tag color="purple">Total Amount edit</Tag>
                                                    )}
                                                </div>

                                                <div style={{ fontSize: '24px', fontWeight: 'bold', color: isLatest ? '#52c41a' : '#1890ff' }}>
                                                    {formatPrice(quote.price)}
                                                </div>

                                                {!isFirstQuote && (
                                                    <div style={{ marginTop: '4px', fontSize: '13px' }}>
                                                        <Text type="secondary">
                                                            Changed from {formatPrice(quote.previousPrice)} ({difference >= 0 ? '+' : '-'}{formatPrice(Math.abs(difference))})
                                                        </Text>
                                                    </div>
                                                )}

                                                <div
                                                    style={{
                                                        marginTop: "4px",
                                                        fontSize: "13px",
                                                        color: "#389e0d",
                                                        fontStyle: "italic",
                                                    }}
                                                >
                                                    {numberToWords(quote.price)} Only
                                                </div>

                                                {quote.description && (
                                                    <div style={{ marginTop: '8px', color: '#666', fontSize: '13px' }}>
                                                        <Text type="secondary">Note: </Text>
                                                        {quote.description}
                                                    </div>
                                                )}

                                                <div style={{ marginTop: '8px', fontSize: '13px' }}>
                                                    <Text type="secondary">
                                                        <UserOutlined /> {isFirstQuote ? 'Added by' : 'Changed by'}{' '}
                                                    </Text>
                                                    <Text strong>{actorName}</Text>
                                                </div>
                                            </div>
                                            <div style={{ textAlign: 'right' }}>
                                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                                    {formatPSTDate(quote.addedAt)}
                                                </Text>
                                            </div>
                                        </div>
                                    </Card>
                                </Timeline.Item>
                            );
                        })}
                </Timeline>
            )}

            {/* Quote Statistics */}
            {lead.quotedValue && lead.quotedValue.length > 0 && (
                <Card
                    size="small"
                    title="Quote Summary"
                    style={{ marginTop: '24px' }}
                >
                    <Row gutter={[16, 16]}>
                        <Col span={12}>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                                    Total Quotes
                                </div>
                                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1890ff' }}>
                                    {lead.quotedValue.length}
                                </div>
                            </div>
                        </Col>
                        <Col span={12}>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                                    Latest Quote
                                </div>
                                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#52c41a' }}>
                                    ${lead.quotedValue[lead.quotedValue.length - 1].price?.toFixed(2)}
                                </div>
                            </div>
                        </Col>

                    </Row>
                </Card>
            )}
        </Card>
    );
};

export default QuotedTab;